import { useCallback, useEffect, useState } from 'react';

import type { DeviceType } from '@weberus/shared-types/src/device-type';
import type { SmartBannerLink } from '@weberus/utils';
import Cookies from 'js-cookie';

export const COOKIE_SMART_BANNER = 'top-smart-banner';

export const useSmartBanner = (os: DeviceType, smartBannerLink?: SmartBannerLink) => {
  const [smartBannerOpen, setSmartBannerOpen] = useState(false);
  const [location, setLocation] = useState({ path: '', params: {} });

  const onSmartBannerClose = useCallback(() => {
    Cookies.set(COOKIE_SMART_BANNER, 'Y', { expires: 1, domain: '.yanolja.com' });
    setSmartBannerOpen(false);
  }, [smartBannerOpen]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const trackcode = Cookies.get('trackcode') || url.searchParams.get('trackcode');
    const userAgent = navigator.userAgent.split(' ');
    const isKakaoEmbedded = userAgent.some((agent) => agent.includes('KAKAOTALK'));
    const isFromKakaoChannel = isKakaoEmbedded && userAgent.some((agent) => agent.includes('ch-home'));
    const isFromHotelsCombined = (trackcode?.startsWith('HC') && trackcode !== 'HC_ETC') || false;
    const isFromNaverHotel = trackcode?.startsWith('naverhotel') || false;
    const isFromTripAdvisor = trackcode === 'tripadvisor';

    /*
     * 카카오 체널로 유입된 경우 COOKIE_SMART_BANNER cookie 만료기간을 1년으로 설정
     * */
    if (isFromKakaoChannel) {
      Cookies.set(COOKIE_SMART_BANNER, 'Y', { domain: '.yanolja.com', expires: 365 });
    }

    /*
     * COOKIE_SMART_BANNER cookie 및 유입채널에 따른 스마트배너 비노출 처리
     * */
    if (
      os === 'etc' ||
      Cookies.get(COOKIE_SMART_BANNER) === 'Y' ||
      isFromHotelsCombined ||
      isFromNaverHotel ||
      isFromTripAdvisor ||
      isFromKakaoChannel
    ) {
      return;
    }

    /*
     * cookie에 trackcode가 있으면 DeepLink 생성시 포함되도록 URLSearchParams에 append 한다.
     * 단, URLSearchParams에 trackcode가 있을 경우는 제외
     * */
    if (trackcode && !url.searchParams.has('trackcode')) {
      url.searchParams.append('trackcode', trackcode);
    }

    /*
     * DeepLink가 있는 지면의 경우에는 wv가 아닌 home 또는 place/detail 등으로 이동해야하기 때문에
     * params smartBannerLink가 우선순위를 가진다.
     * */
    setLocation({
      path: smartBannerLink?.path ?? `wv?url=${url.origin}${url.pathname}`,
      params: smartBannerLink?.params ?? Object.fromEntries(url.searchParams),
    });

    setSmartBannerOpen(true);
  }, []);

  return {
    path: location.path,
    params: location.params,
    smartBannerOpen,
    setSmartBannerOpen,
    onSmartBannerClose,
  };
};
